/**
 * The catalogue as the wardrobe picker walks it: women or men, then tops or bottoms, then the kind
 * of garment. Built on the `gender` and `category` facets the admin maintains rather than on
 * collections — the collection tree is flat here, its women/men split only an ordering.
 *
 * This file only says which garment types are tops and which are bottoms. Which of them a gender
 * actually has is asked of the search index (`searchFacetValues`), so a men's range that grows a
 * category appears in the picker without a change here. `exld` is a product line rather than a
 * garment type, and the accessories (socks, caps, bags) have no fit worth keeping as a reference,
 * so neither is listed.
 */

export type SizeProfileGender = 'women' | 'men'

export interface SizeProfileGenderOption {
  value: SizeProfileGender
  labelKey: string
  /** `gender` facet values whose products count as this gender's. Unisex counts for both. */
  facetValueCodes: string[]
}

export const SIZE_PROFILE_GENDERS: readonly SizeProfileGenderOption[] = [
  { value: 'women', labelKey: 'collections.gender.women', facetValueCodes: ['women', 'unisex'] },
  { value: 'men', labelKey: 'collections.gender.men', facetValueCodes: ['men', 'unisex'] },
]

export interface SizeProfileCategory {
  /** The `category` facet value code. */
  code: string
  labelKey: string
}

export interface SizeProfileGroup {
  key: 'tops' | 'bottoms'
  labelKey: string
  icon: string
  categories: readonly SizeProfileCategory[]
}

export const SIZE_PROFILE_GROUPS: readonly SizeProfileGroup[] = [
  {
    key: 'tops',
    labelKey: 'header.mega.heading.tops',
    icon: 'ph:t-shirt-light',
    categories: [
      { code: 'tees-tanks', labelKey: 'header.mega.link.tees_tanks' },
      { code: 'sports-bras', labelKey: 'product.category.sportbra' },
      { code: 'sweatshirts', labelKey: 'product.category.sweatshirt' },
      { code: 'longsleeve', labelKey: 'product.category.longsleeve' },
      { code: 'jackets', labelKey: 'product.category.jacket' },
    ],
  },
  {
    key: 'bottoms',
    labelKey: 'header.mega.heading.bottoms',
    icon: 'ph:pants-light',
    categories: [
      { code: 'leggings', labelKey: 'product.category.leggings' },
      { code: 'shorts', labelKey: 'product.category.shorts' },
      { code: 'joggers', labelKey: 'product.category.joggers' },
      { code: 'skirts', labelKey: 'product.category.skirt' },
      // Where the menu files it too.
      { code: 'jumpsuit', labelKey: 'header.mega.link.jumpsuits' },
    ],
  },
]

interface ColouredFacetValue { name: string, facet: { code: string } }

/**
 * The colour a piece is sold as ("Maroon"): the product's `color` facet value, which is how the
 * shop names it everywhere else, else the variant's (a colour family, "Red"), else nothing.
 */
export function variantColourName(variant: {
  facetValues?: readonly ColouredFacetValue[]
  product: { facetValues?: readonly ColouredFacetValue[] }
}): string | undefined {
  const colourOf = (values?: readonly ColouredFacetValue[]) => values?.find(value => value.facet.code === 'color')?.name
  return colourOf(variant.product.facetValues) ?? colourOf(variant.facetValues)
}

/**
 * The size a variant is in, by name ("M"), or undefined for a variant without a size option.
 *
 * The group is recognised the way every other size surface recognises it (`isSizeGroup`): groups
 * are per product, coded `<product-slug>-size`, so no one code names them all.
 */
export function variantSizeName(variant: { options: readonly { name: string, group: { code: string, name: string } }[] }): string | undefined {
  return variant.options.find(option => isSizeGroup(option.group.name, option.group.code))?.name
}
